import React from "react";

interface BlockSkeletonProps {
  blockType: string;
  chartType?: string;
  title?: string;
  label?: string;
  blockId: string;
}

const SkeletonPulse: React.FC<{ className?: string; style?: React.CSSProperties }> = ({
  className = "",
  style,
}) => (
  <div className={`animate-pulse rounded bg-main-foreground/[0.035] ${className}`} style={style} />
);

const MetricCardSkeleton: React.FC<{ label?: string }> = ({ label }) => (
  <div className="themed-dashboard-card p-0 h-full">
    <div className="flex flex-col justify-start text-left w-full h-full p-4">
      <div className="flex items-start justify-between gap-2 mt-1">
        <div className="text-card-muted-foreground font-medium text-sm truncate opacity-[0.21]">
          {label ?? <SkeletonPulse className="h-3 w-20" />}
        </div>
        <SkeletonPulse className="h-6 w-6 rounded-full shrink-0" />
      </div>
      <div className="flex-1 flex items-center">
        <SkeletonPulse className="h-8 w-3/5" />
      </div>
      <SkeletonPulse className="h-3 w-2/5 mt-1" />
    </div>
  </div>
);

const ChartSkeletonWrapper: React.FC<{
  title?: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="themed-dashboard-card h-full">
    {title ? (
      <h2 className="themed-card-title opacity-[0.14]">{title}</h2>
    ) : (
      <SkeletonPulse className="h-5 w-2/5 mb-4" />
    )}
    {children}
  </div>
);

const BarChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex flex-col justify-end gap-1 pb-2 px-2">
    <div className="flex items-end gap-2 flex-1">
      {[40, 65, 50, 80, 55, 70, 45, 75, 60, 85, 50, 65].map((h, i) => (
        <SkeletonPulse
          key={i}
          className="flex-1 rounded-t rounded-b-none"
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
    <SkeletonPulse className="h-px w-full mt-1" />
  </div>
);

const LineChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex flex-col justify-end pb-2 px-2 relative">
    <svg
      viewBox="0 0 300 120"
      className="w-full h-full animate-pulse"
      preserveAspectRatio="none"
      style={{ minHeight: 0 }}
    >
      <polyline
        points="0,90 30,70 60,80 90,40 120,55 150,30 180,50 210,25 240,45 270,35 300,20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        className="text-main-foreground/[0.052]"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon
        points="0,120 0,90 30,70 60,80 90,40 120,55 150,30 180,50 210,25 240,45 270,35 300,20 300,120"
        className="text-main-foreground/[0.018]"
        fill="currentColor"
      />
    </svg>
    <SkeletonPulse className="h-px w-full mt-1" />
  </div>
);

const PieChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex items-center justify-center pb-2">
    <div className="animate-pulse" style={{ width: "55%", maxWidth: 200, aspectRatio: "1" }}>
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="currentColor"
          strokeWidth="40"
          className="text-main-foreground/[0.035]"
          strokeDasharray="70 213"
          strokeDashoffset="0"
        />
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="currentColor"
          strokeWidth="40"
          className="text-main-foreground/[0.021]"
          strokeDasharray="90 193"
          strokeDashoffset="-70"
        />
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="currentColor"
          strokeWidth="40"
          className="text-main-foreground/[0.049]"
          strokeDasharray="60 223"
          strokeDashoffset="-160"
        />
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="currentColor"
          strokeWidth="40"
          className="text-main-foreground/[0.028]"
          strokeDasharray="63 220"
          strokeDashoffset="-220"
        />
      </svg>
    </div>
  </div>
);

const DoughnutChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex items-center justify-center pb-2">
    <div className="animate-pulse" style={{ width: "55%", maxWidth: 200, aspectRatio: "1" }}>
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle
          cx="50"
          cy="50"
          r="38"
          fill="none"
          stroke="currentColor"
          strokeWidth="18"
          className="text-main-foreground/[0.035]"
          strokeDasharray="60 179"
          strokeDashoffset="0"
        />
        <circle
          cx="50"
          cy="50"
          r="38"
          fill="none"
          stroke="currentColor"
          strokeWidth="18"
          className="text-main-foreground/[0.021]"
          strokeDasharray="75 164"
          strokeDashoffset="-60"
        />
        <circle
          cx="50"
          cy="50"
          r="38"
          fill="none"
          stroke="currentColor"
          strokeWidth="18"
          className="text-main-foreground/[0.049]"
          strokeDasharray="50 189"
          strokeDashoffset="-135"
        />
        <circle
          cx="50"
          cy="50"
          r="38"
          fill="none"
          stroke="currentColor"
          strokeWidth="18"
          className="text-main-foreground/[0.028]"
          strokeDasharray="54 185"
          strokeDashoffset="-185"
        />
      </svg>
    </div>
  </div>
);

const ScatterChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex flex-col justify-end pb-2 px-2 relative">
    <svg
      viewBox="0 0 300 150"
      className="w-full h-full animate-pulse"
      preserveAspectRatio="xMidYMid meet"
      style={{ minHeight: 0 }}
    >
      {[
        [30, 110],
        [55, 85],
        [80, 95],
        [100, 60],
        [130, 70],
        [150, 40],
        [175, 55],
        [200, 30],
        [220, 50],
        [245, 35],
        [260, 65],
        [280, 45],
        [140, 100],
        [190, 80],
        [110, 45],
        [70, 50],
      ].map(([cx, cy], i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r="5"
          className="text-main-foreground/[0.042]"
          fill="currentColor"
        />
      ))}
    </svg>
    <SkeletonPulse className="h-px w-full mt-1" />
  </div>
);

const FunnelChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-1.5 pb-2 px-6">
    {[100, 78, 56, 38, 22].map((w, i) => (
      <SkeletonPulse key={i} className="h-8 rounded-md" style={{ width: `${w}%` }} />
    ))}
  </div>
);

const TreemapChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex flex-col gap-1 pb-2 px-2">
    <div className="flex gap-1 flex-1">
      <SkeletonPulse className="rounded-md" style={{ flex: 4 }} />
      <div className="flex flex-col gap-1" style={{ flex: 3 }}>
        <SkeletonPulse className="rounded-md flex-1" />
        <SkeletonPulse className="rounded-md" style={{ flex: 0.6 }} />
      </div>
    </div>
    <div className="flex gap-1" style={{ flex: 0.5 }}>
      <SkeletonPulse className="rounded-md flex-1" />
      <SkeletonPulse className="rounded-md" style={{ flex: 0.7 }} />
      <SkeletonPulse className="rounded-md" style={{ flex: 1.2 }} />
    </div>
  </div>
);

const SankeyChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex items-center pb-2 px-4">
    <svg
      viewBox="0 0 340 140"
      className="w-full h-full animate-pulse"
      preserveAspectRatio="xMidYMid meet"
      style={{ minHeight: 0 }}
    >
      {/* Left column nodes */}
      <rect
        x="0"
        y="5"
        width="18"
        height="45"
        rx="3"
        className="text-main-foreground/[0.042]"
        fill="currentColor"
      />
      <rect
        x="0"
        y="60"
        width="18"
        height="30"
        rx="3"
        className="text-main-foreground/[0.035]"
        fill="currentColor"
      />
      <rect
        x="0"
        y="100"
        width="18"
        height="35"
        rx="3"
        className="text-main-foreground/[0.028]"
        fill="currentColor"
      />
      {/* Middle column nodes */}
      <rect
        x="161"
        y="15"
        width="18"
        height="55"
        rx="3"
        className="text-main-foreground/[0.042]"
        fill="currentColor"
      />
      <rect
        x="161"
        y="80"
        width="18"
        height="40"
        rx="3"
        className="text-main-foreground/[0.035]"
        fill="currentColor"
      />
      {/* Right column nodes */}
      <rect
        x="322"
        y="10"
        width="18"
        height="50"
        rx="3"
        className="text-main-foreground/[0.042]"
        fill="currentColor"
      />
      <rect
        x="322"
        y="70"
        width="18"
        height="35"
        rx="3"
        className="text-main-foreground/[0.028]"
        fill="currentColor"
      />
      <rect
        x="322"
        y="115"
        width="18"
        height="20"
        rx="3"
        className="text-main-foreground/[0.021]"
        fill="currentColor"
      />
      {/* Flow bands */}
      <path
        d="M18,15 C90,15 90,25 161,25"
        stroke="currentColor"
        strokeWidth="12"
        fill="none"
        className="text-main-foreground/[0.021]"
        strokeLinecap="round"
      />
      <path
        d="M18,35 C90,35 90,55 161,55"
        stroke="currentColor"
        strokeWidth="10"
        fill="none"
        className="text-main-foreground/[0.018]"
        strokeLinecap="round"
      />
      <path
        d="M18,70 C90,70 90,90 161,90"
        stroke="currentColor"
        strokeWidth="8"
        fill="none"
        className="text-main-foreground/[0.014]"
        strokeLinecap="round"
      />
      <path
        d="M18,115 C90,115 90,100 161,100"
        stroke="currentColor"
        strokeWidth="8"
        fill="none"
        className="text-main-foreground/[0.018]"
        strokeLinecap="round"
      />
      <path
        d="M179,30 C250,30 250,25 322,25"
        stroke="currentColor"
        strokeWidth="14"
        fill="none"
        className="text-main-foreground/[0.021]"
        strokeLinecap="round"
      />
      <path
        d="M179,55 C250,55 250,80 322,80"
        stroke="currentColor"
        strokeWidth="10"
        fill="none"
        className="text-main-foreground/[0.018]"
        strokeLinecap="round"
      />
      <path
        d="M179,95 C250,95 250,120 322,120"
        stroke="currentColor"
        strokeWidth="6"
        fill="none"
        className="text-main-foreground/[0.014]"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

const HorizontalBarChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex flex-col justify-center gap-2 pb-2 px-2">
    {[85, 70, 55, 45, 30, 60, 40].map((w, i) => (
      <SkeletonPulse key={i} className="h-5 rounded-r rounded-l-none" style={{ width: `${w}%` }} />
    ))}
  </div>
);

const WaterfallChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex items-end gap-1.5 pb-2 px-2">
    {[
      { topPct: 0, heightPct: 78, opacity: 0.042 },
      { topPct: 6, heightPct: 14, opacity: 0.038 },
      { topPct: 20, heightPct: 12, opacity: 0.038 },
      { topPct: 32, heightPct: 18, opacity: 0.034 },
      { topPct: 50, heightPct: 10, opacity: 0.034 },
      { topPct: 0, heightPct: 60, opacity: 0.042 },
    ].map((b, i) => (
      <div key={i} className="relative flex-1 h-full">
        <SkeletonPulse
          className="rounded-sm absolute left-0 right-0"
          style={{ top: `${b.topPct}%`, height: `${b.heightPct}%`, opacity: 0.7 + b.opacity * 4 }}
        />
      </div>
    ))}
  </div>
);

const BridgeChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex items-end gap-1.5 pb-2 px-3">
    {[
      { topPct: 0, heightPct: 70 },
      { topPct: 18, heightPct: 14 },
      { topPct: 30, heightPct: 10 },
      { topPct: 36, heightPct: 12 },
      { topPct: 0, heightPct: 64 },
    ].map((b, i) => (
      <div key={i} className="relative flex-1 h-full">
        <SkeletonPulse
          className="rounded-sm absolute left-0 right-0"
          style={{ top: `${b.topPct}%`, height: `${b.heightPct}%` }}
        />
      </div>
    ))}
  </div>
);

const GanttChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex flex-col justify-center gap-2 py-2 px-2">
    {[
      { leftPct: 0, widthPct: 28 },
      { leftPct: 14, widthPct: 34 },
      { leftPct: 32, widthPct: 40 },
      { leftPct: 56, widthPct: 26 },
      { leftPct: 72, widthPct: 22 },
    ].map((b, i) => (
      <div key={i} className="relative flex-1 min-h-0 max-h-6 w-full">
        <SkeletonPulse
          className="rounded-sm absolute top-0 bottom-0"
          style={{ left: `${b.leftPct}%`, width: `${b.widthPct}%` }}
        />
      </div>
    ))}
  </div>
);

const BubbleChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex flex-col justify-end pb-2 px-2 relative">
    <svg
      viewBox="0 0 300 150"
      className="w-full h-full animate-pulse"
      preserveAspectRatio="xMidYMid meet"
      style={{ minHeight: 0 }}
    >
      {[
        [50, 100, 12],
        [80, 60, 18],
        [130, 80, 10],
        [160, 35, 22],
        [200, 55, 14],
        [230, 90, 16],
        [260, 40, 11],
        [110, 45, 15],
        [190, 100, 9],
        [270, 70, 20],
      ].map(([cx, cy, r], i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={r}
          className="text-main-foreground/[0.035]"
          fill="currentColor"
        />
      ))}
    </svg>
    <SkeletonPulse className="h-px w-full mt-1" />
  </div>
);

const GeoChartSkeleton: React.FC = () => (
  <div className="flex-1 min-h-0 flex flex-col justify-end pb-2 px-2 relative">
    <svg
      viewBox="0 0 300 150"
      className="w-full h-full animate-pulse"
      preserveAspectRatio="xMidYMid meet"
      style={{ minHeight: 0 }}
    >
      {/* Loose landmass blobs suggesting a map, shaded at varying intensity */}
      <path
        d="M20 40 Q45 18 85 25 Q120 30 130 55 Q135 85 105 100 Q70 112 40 95 Q12 75 20 40 Z"
        className="text-main-foreground/[0.05]"
        fill="currentColor"
      />
      <path
        d="M150 30 Q185 15 225 28 Q260 40 265 70 Q262 100 230 110 Q195 118 165 100 Q140 78 150 30 Z"
        className="text-main-foreground/[0.03]"
        fill="currentColor"
      />
      <path
        d="M90 110 Q120 100 150 112 Q175 122 170 138 Q150 148 115 144 Q88 136 90 110 Z"
        className="text-main-foreground/[0.04]"
        fill="currentColor"
      />
    </svg>
    <SkeletonPulse className="h-px w-full mt-1" />
  </div>
);

function getChartSkeleton(chartType?: string): React.ReactNode {
  switch (chartType) {
    case "line":
    case "area":
      return <LineChartSkeleton />;
    case "pie":
      return <PieChartSkeleton />;
    case "doughnut":
      return <DoughnutChartSkeleton />;
    case "scatter":
      return <ScatterChartSkeleton />;
    case "bubble":
      return <BubbleChartSkeleton />;
    case "horizontalBar":
      return <HorizontalBarChartSkeleton />;
    case "funnel":
      return <FunnelChartSkeleton />;
    case "treemap":
      return <TreemapChartSkeleton />;
    case "sankey":
      return <SankeyChartSkeleton />;
    case "waterfall":
      return <WaterfallChartSkeleton />;
    case "bridge":
      return <BridgeChartSkeleton />;
    case "gantt":
      return <GanttChartSkeleton />;
    case "choropleth":
    case "bubbleMap":
      return <GeoChartSkeleton />;
    case "bar":
    default:
      return <BarChartSkeleton />;
  }
}

const ChartSkeleton: React.FC<{ title?: string; chartType?: string }> = ({ title, chartType }) => (
  <ChartSkeletonWrapper title={title}>{getChartSkeleton(chartType)}</ChartSkeletonWrapper>
);

const DataTableSkeleton: React.FC<{ title?: string }> = ({ title }) => (
  <div className="themed-dashboard-card h-full">
    {title ? (
      <h2 className="themed-card-title opacity-[0.14]">{title}</h2>
    ) : (
      <SkeletonPulse className="h-5 w-2/5 mb-4" />
    )}
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex gap-3 pb-2 border-b border-main-foreground/[0.035]">
        {[1, 2, 3, 4].map(i => (
          <SkeletonPulse key={i} className="h-4 flex-1" />
        ))}
      </div>
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="flex gap-3 py-2.5 border-b border-main-foreground/[0.018]">
          {[1, 2, 3, 4].map(j => (
            <SkeletonPulse key={j} className="h-3 flex-1" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const BlockSkeleton: React.FC<BlockSkeletonProps> = ({
  blockType,
  chartType,
  title,
  label,
}) => {
  const renderSkeleton = () => {
    switch (blockType) {
      case "metric_card":
        return <MetricCardSkeleton label={label} />;
      case "chart":
        return <ChartSkeleton title={title} chartType={chartType} />;
      case "data_table":
        return <DataTableSkeleton title={title} />;
      default:
        return (
          <div className="themed-dashboard-card h-full">
            <SkeletonPulse className="h-full w-full" />
          </div>
        );
    }
  };

  return <div className="h-full">{renderSkeleton()}</div>;
};
